import { SearchActions } from './actions'
import { ITag } from '@/interfaces/tag'


interface IAggregation {
    id: string,
    label: string,
    values: ITag[],
    type: string
}
interface IState {
    searching: boolean,
    searchingRelated: boolean,
    aggregations: IAggregation[],
    items: any[],
    pageItems: { [page: number]: any[] },
    totalCount?: number,
    relationsItems: any[] | null,
    relationsTotalCount: number,
    dosLoading?: boolean,
    navURL?: string,
    scrolledDown?: boolean,
    error?: any,
    query: {
        query: string,
        categories: string[],
        filters: any[],
        sort_by: string | null,
        sort_order: 'asc' | 'desc',
        page: number,
        pageSize: number,
    },
    selectedItem: any,
    parentDetails: { [identifier: string]: any },
    digitalObjectSourcePaths: { [identifier: string]: any },
    geoFilter: { minLon: number; minLat: number; maxLon: number; maxLat: number } | null,
}


const initialState: IState = {
    searching: false,
    searchingRelated: false,
    items: [] as any[],
    pageItems: {} as { [page: number]: any[] },
    aggregations: [
    ] as IAggregation[],
    relationsItems: null,
    relationsTotalCount: 0,
    query: {
        query: '**',
        categories: [] as string[],
        filters: [] as any[],
        sort_by: null,
        sort_order: 'asc' as 'asc' | 'desc',
        page: 0,
        pageSize: 10,
    },
    selectedItem: null,
    parentDetails: {},
    digitalObjectSourcePaths: {},
    geoFilter: null,
}


export const searchReducer = (state = initialState, action: any) => {
    switch (action.type) {

        case SearchActions.SEARCH_STARTED:
            return {
                ...state,
                searching: true
            }
        case SearchActions.FETCH_DOS:
            return {
                ...state,
                dosLoading: true
            }
        case SearchActions.FETCH_DOS_SUCCESS: {
            const digitalObjectsData = action.payload?.items || action.payload?.digitalObjects || action.payload;
            // Handle new pagination fields from API: totalOfDigitalObjects, page, pageSize
            const totalCount = action.payload?.totalOfDigitalObjects || action.payload?.totalCount || action.payload?.total || digitalObjectsData?.length || 0;
            const currentPage = action.payload?.page !== undefined ? action.payload.page : 0;
            const pageSize = action.payload?.pageSize || 25;

            return {
                ...state,
                dosLoading: false,
                selectedItem: {
                    ...state.selectedItem,
                    relatedDigitalObjects: {
                        items: digitalObjectsData,
                        totalCount: totalCount,
                        totalOfDigitalObjects: action.payload?.totalOfDigitalObjects, // Store the new field
                        page: currentPage, // Store current page from API
                        pageSize: pageSize, // Store page size from API
                    }
                }
            }
        }
        case SearchActions.FETCH_DOS_ERROR:
            return {
                ...state,
                dosLoading: false
            }
        case SearchActions.INITIAL_SEARCH:
            return {
                ...state,
                items: [],
                pageItems: {} as { [page: number]: any[] },
                totalCount: 0,
                aggregations: [],
                query: {
                    ...state.query,
                    page: 0,
                },
                navURL: '/results',
                selectedItem: null,
            }
        case SearchActions.RELATED_SEARCH:
            return {
                ...state,
                items: [],
                pageItems: {} as { [page: number]: any[] },
                query: {
                    ...state.query,
                    page: 0,
                },
                navURL: '/results',
            }
        case SearchActions.RELATED_SEARCH_FILTERED:
            return {
                ...state,
                items: [],
                pageItems: {} as { [page: number]: any[] },
                query: {
                    ...state.query,
                    page: 0,
                },
                navURL: '/results',
            }
        case SearchActions.RELATED_SEARCH_SUCCESS:
            {
                const identifier = action.payload.identifier;
                const mainItem = action.payload.result?.items?.filter((item: any) => item?.identifier === identifier)[0];

                if (!mainItem) {
                    const fallbackItems = action.payload.result?.items || [];
                    return {
                        ...state,
                        searching: false,
                        items: fallbackItems,
                        pageItems: { 0: fallbackItems },
                        totalCount: action.payload.result.total,
                    };
                }
                const parents = mainItem.parents || [];
                const children = mainItem.children || [];
                const parentsAndChildren = [...parents, ...children];
                const relatedItems = parentsAndChildren.filter((item: any) => (item != null)) || [];
                return {
                    ...state,
                    searching: false,
                    items: relatedItems,
                    pageItems: { 0: relatedItems },
                    totalCount: relatedItems.length,
                }
            }
        case SearchActions.RELATED_SEARCH_FILTERED_SUCCESS:
            {
                const items = action.payload.result?.items || []
                return {
                    ...state,
                    searching: false,
                    items: items,
                    pageItems: { 0: items },
                    totalCount: items.length,
                }
            }
        case SearchActions.SET_SEARCH_TERM:
            return {
                ...state,
                query: {
                    ...state.query,
                    query: `*${action.payload}*`
                }
            }
        case SearchActions.SET_SEARCH_RELATED:
            return {
                ...state,
                searchingRelated: action.payload
            }
        case SearchActions.SET_SELECTED:
            return {
                ...state,
                selectedItem: action.payload
            }
        case SearchActions.CLEAR_SELECTED:
            return {
                ...state,
                selectedItem: null
            }
        case SearchActions.RESET_SEARCH:
            return {
                ...state,
                query: {
                    ...state.query,
                    filters: [],
                    categories: [],
                    query: '**'
                }
            }
        case SearchActions.ADD_TAG:
            {
                const _key = action.payload.key;
                const field = _key.indexOf('#') > 0 ? _key.split('#')[1] : _key;
                const tagExists = state.query?.filters?.some(tag => tag.field === field);
                let mapped: any;
                switch (action.payload.type) {
                    case 'terms':
                        mapped = {
                            field: field,
                            key: field,
                            values: [action.payload.id],
                            type: "terms",
                        }
                        if (tagExists) {
                            return {
                                ...state,
                                scrolledDown: false,
                                query: {
                                    ...state.query,
                                    filters: state.query.filters.map((filter: any) => {
                                        if (filter.key === field) {
                                            return { ...filter, values: [...filter.values, action.payload.id] };
                                        }
                                        return filter;
                                    })
                                }
                            }
                        } else {
                            return {
                                ...state,
                                query: {
                                    ...state.query,
                                    filters: [...state.query.filters, mapped]
                                }
                            }
                        }
                    case 'range':
                        mapped = {
                            field: action.payload.key,
                            values: [action.payload.min,
                            action.payload.max],
                            type: "range"
                        }
                        if (tagExists) {
                            return {
                                ...state,
                                scrolledDown: false,
                                query: {
                                    ...state.query,
                                    filters: state.query.filters.map((filter: any) => {
                                        if (filter.field === action.payload.key) {
                                            return mapped;
                                        }
                                        return filter;
                                    })
                                }
                            }
                        }
                        return {
                            ...state,
                            scolledDown: false,
                            query: {
                                ...state.query,
                                filters: [...state.query.filters, mapped]
                            }
                        }
                    default:
                        return state;
                }
            }
        case SearchActions.UPDATE_FILTER_VALUES: {
            let hit = false;
            const res = state.query.filters.map((filter: any) => {
                if (filter.key === action.payload.key) {
                    hit = true;
                    return { ...filter, values: action.payload.values };
                }
                return filter;
            });
            if (!hit) {
                res.push({ id: action.payload.key, key: action.payload.key, values: action.payload.values });
            }

            return {
                ...state,
                query: {
                    ...state.query,
                    filters: res
                }
            };
        }
        case SearchActions.UPDATE_FILTER_RANGE: {
            let hitRange = false;
            const resRange = state.query.filters.map((filter: any) => {
                if (filter.key === action.payload.key) {
                    hitRange = true;
                    return { ...filter, values: [action.payload.min, action.payload.max] };
                }
                return filter;
            });
            if (!hitRange) {
                resRange.push({ id: action.payload.key, key: action.payload.key, values: [action.payload.min, action.payload.max] });
            }

            return {
                ...state,
                query: {
                    ...state.query,
                    filters: resRange
                }
            };
        }
        case SearchActions.ADD_CATEGORY: {
            const catExists = state.query.categories.some(cat => cat === action.payload);
            if (catExists) {
                return state;
            } else {
                return {
                    ...state,
                    query: { ...state.query, categories: [...state.query.categories, action.payload] }
                };
            }
        }
        case SearchActions.REMOVE_CATEGORY:
            return {
                ...state,
                query: { ...state.query, categories: state.query.categories.filter(cat => cat !== action.payload) }
            };
        case SearchActions.CLEAR_CATEGORIES:
            return {
                ...state,
                query: {
                    ...state.query,
                    filters: state.query.filters.filter((filter: any) => filter.field !== 'indexname')
                }
            }
        case SearchActions.REMOVE_TAG:
            {
                const _field = action.payload.field;
                const field = _field.indexOf('#') > 0 ? _field.split('#')[1] : _field;
                return {
                    ...state,
                    query: {
                        ...state.query,
                        filters: state.query.filters.map((filter: any) => {
                            if (filter.field === field) {
                                if (action.payload.value) {
                                    const values = filter.values.filter((v: string) => v !== action.payload.value);
                                    if (values.length === 0) {
                                        return null;
                                    }
                                    return { ...filter, values: filter.values.filter((v: string) => v !== action.payload.value) };
                                } else {
                                    return null;
                                }
                            }
                            return filter;
                        }).filter((f: any) => f !== null)
                    }
                }
            }
        case SearchActions.REMOVE_RANGE_TAG:
            return {
                ...state,
                query: {
                    ...state.query,
                    filters: state.query.filters.filter((filter: any) => filter.field !== action.payload.field)
                }
            }
        case SearchActions.CLEAR_SEARCH_TERM:
            return {
                ...state,
                query: {
                    ...state.query,
                    searchTerm: ''
                }
            }
        case SearchActions.FETCH_DETAILS:
            return {
                ...state,
                dosLoading: true,
                itemDetails: [],
            }
        case SearchActions.FETCH_DETAILS_SUCCESS:
            {
                const metadata = structuredClone(action.payload.metadata);
                return {
                    ...state,
                    itemDetails: metadata
                }
            }
        case SearchActions.FETCH_PARENT_DETAILS_SUCCESS:
            {
                // Store parent details separately from main item details
                const metadata = structuredClone(action.payload.metadata);
                const requestIdentifier = action.payload.requestIdentifier;
                return {
                    ...state,
                    parentDetails: {
                        ...state.parentDetails,
                        [requestIdentifier]: metadata
                    }
                }
            }
        case SearchActions.FETCH_PARENT_DETAILS_ERROR:
            {
                // Store error information for the specific identifier
                const requestIdentifier = action.payload.requestIdentifier;
                const errorData = {
                    error: true,
                    is404: action.payload.is404,
                    message: action.payload.message,
                    timestamp: new Date().toISOString()
                };
                return {
                    ...state,
                    parentDetails: {
                        ...state.parentDetails,
                        [requestIdentifier]: errorData
                    }
                }
            }
        case SearchActions.INITIAL_SEARCH_SUCCESS:
            {
                const payloadTotal =
                    action.payload?.total ??
                    action.payload?.totalCount ??
                    action.payload?.total_count ??
                    action.payload?.totalHits ??
                    action.payload?.hits?.total ??
                    action.payload?.items?.length ??
                    0;
                const page0Items = action.payload.items || [];
            return {
                ...state,
                searching: false,
                items: page0Items,
                pageItems: { 0: page0Items },
                    totalCount: payloadTotal,
                aggregations: action.payload.aggregations,
                relationsItems: null,
                relationsTotalCount: 0,
                }
            }
        case SearchActions.APPENDING_SEARCH_SUCCESS:
            {
                const payloadItems = action.payload?.items || [];
                const fetchedPage: number = action.payload?._fetchedPage ?? 0;
                const payloadTotal =
                    action.payload?.total ??
                    action.payload?.totalCount ??
                    action.payload?.total_count ??
                    action.payload?.totalHits ??
                    action.payload?.hits?.total;
                // Keep accumulated items array for backward compatibility (used by map filter saga)
                const existingIds = new Set(
                    state.items.map((item: any) => item?.identifier).filter(Boolean)
                );
                const uniqueNewItems = payloadItems.filter(
                    (item: any) => !item?.identifier || !existingIds.has(item.identifier)
                );
                return {
                    ...state,
                    searching: false,
                    items: [...state.items, ...uniqueNewItems],
                    // Store the page's items directly — no deduplication, exact API response
                    pageItems: {
                        ...state.pageItems,
                        [fetchedPage]: payloadItems,
                    },
                    totalCount: payloadTotal ?? state.totalCount,
                }
            }

        case SearchActions.SEARCH_ERROR:
            return {
                ...state,
                searching: false,
                error: action.payload
            }
        case SearchActions.FILTERED_SEARCH_SUCCESS:
            {
                const payloadTotal =
                    action.payload?.total ??
                    action.payload?.totalCount ??
                    action.payload?.total_count ??
                    action.payload?.totalHits ??
                    action.payload?.hits?.total ??
                    action.payload?.items?.length ??
                    0;
                const filteredItems = action.payload.items || [];
                return {
                ...state,
                searching: false,
                items: filteredItems,
                pageItems: { 0: filteredItems },
                    totalCount: payloadTotal,
                aggregations: state.aggregations,
                relationsItems: null,
                relationsTotalCount: 0,
                }
            }
        case SearchActions.SET_PAGE:
            return {
                ...state,
                query: {
                    ...state.query,
                    page: action.payload
                }
            }
        case SearchActions.SET_PAGE_SIZE:
            return {
                ...state,
                items: [],
                pageItems: {} as { [page: number]: any[] },
                totalCount: 0,
                query: {
                    ...state.query,
                    pageSize: action.payload,
                    page: 0,
                }
            }
        case SearchActions.SET_URL:
            return {
                ...state,
                navURL: action.payload
            }
        case SearchActions.SET_SCROLLED_DOWN:
            return {
                ...state,
                scrolledDown: action.payload
            }
        case SearchActions.SET_DOWNLOAD_STATUS:
            return {
                ...state,
                items: state.items.map((item: any) => {
                    if (item.id === action.payload.id) {
                        return { ...item, status: action.payload.status };
                    }
                    return item;
                })
            }
        case SearchActions.CLEANUP_PARENT_DETAILS:
            {
                // Keep only the most recent 20 entries to prevent memory bloat
                const entries = Object.entries(state.parentDetails);
                const recentEntries = entries.slice(-20);
                const cleanedParentDetails = Object.fromEntries(recentEntries);

                console.log(`Cleaned parentDetails: ${entries.length} -> ${recentEntries.length} entries`);

                return {
                    ...state,
                    parentDetails: cleanedParentDetails
                }
            }
        case SearchActions.FETCH_DIGITAL_OBJECT_SOURCE_PATHS_SUCCESS:
            {
                const { identifier, data } = action.payload;
                return {
                    ...state,
                    digitalObjectSourcePaths: {
                        ...state.digitalObjectSourcePaths,
                        [identifier]: data
                    }
                }
            }
        case SearchActions.FETCH_DIGITAL_OBJECT_SOURCE_PATHS_ERROR:
            {
                const { identifier, error } = action.payload;
                return {
                    ...state,
                    digitalObjectSourcePaths: {
                        ...state.digitalObjectSourcePaths,
                        [identifier]: { error: error }
                    }
                }
            }
        case SearchActions.SET_GEO_FILTER:
            return {
                ...state,
                geoFilter: action.payload
            }
        case SearchActions.CLEAR_GEO_FILTER:
            return {
                ...state,
                geoFilter: null,
                relationsItems: null,
                relationsTotalCount: 0,
            }
        case SearchActions.CHILDREN_GEO_SEARCH_SUCCESS:
            {
                const payloadTotal =
                    action.payload?.total ??
                    action.payload?.totalCount ??
                    action.payload?.total_count ??
                    action.payload?.totalHits ??
                    action.payload?.items?.length ??
                    0;
                return {
                    ...state,
                    searching: false,
                    relationsItems: action.payload.items ?? [],
                    relationsTotalCount: payloadTotal,
                };
            }
        case SearchActions.CHILDREN_GEO_SEARCH_ERROR:
            return {
                ...state,
                searching: false,
                error: action.payload,
            };
        case SearchActions.CLEAR_RELATIONS_FILTER:
            return {
                ...state,
                relationsItems: null,
                relationsTotalCount: 0,
            };
        case SearchActions.SET_SORT:
            return {
                ...state,
                query: {
                    ...state.query,
                    sort_by: action.payload.sort_by,
                    sort_order: action.payload.sort_order,
                }
            }
        default:
            return state;
    }
}

